import { useState, useEffect } from 'react';
import { 
  Card, 
  CardHeader, 
  CardTitle, 
  CardContent, 
  Button, 
  StatusBadge,
  Separator
} from '@etherisc/ui-kit';
import { 
  ArrowLeftIcon, 
  MapIcon, 
  DownloadIcon, 
  FileIcon,
  CalendarIcon,
  BarChart3Icon,
  MapPinIcon,
  InfoIcon,
  LoaderIcon,
  LayersIcon,
  EyeIcon,
  XIcon
} from 'lucide-react';
import { shapefileService } from '../../services/climate/spatial/ShapefileService';
import { BoundingBoxMap } from './BoundingBoxMap';
import type { Shapefile, SpatialFeature } from '../../types/shapefile';

interface ShapefileDetailViewProps {
  shapefile: Shapefile;
  onBack: () => void;
  onDownload: (shapefile: Shapefile) => void;
  loading?: boolean;
}

interface ChirpsDownloadResponse {
  success: boolean;
  job_id: string;
  message: string;
  status: string;
  processing_result?: {
    success: boolean;
    error?: string;
    job_id: string;
  };
  timestamp: string;
}

export function ShapefileDetailView({ 
  shapefile: initialShapefile, 
  onBack, 
  onDownload, 
  loading: externalLoading = false 
}: ShapefileDetailViewProps) {
  const [shapefile, setShapefile] = useState<Shapefile>(initialShapefile);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedFeature, setSelectedFeature] = useState<SpatialFeature | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadResponse, setDownloadResponse] = useState<ChirpsDownloadResponse | null>(null);

  // Fetch complete shapefile details when component loads
  useEffect(() => {
    const fetchShapefileDetails = async () => {
      try {
        setLoading(true);
        setError(null);
        const detailedShapefile = await shapefileService.getShapefileById(initialShapefile.id);
        setShapefile(detailedShapefile);
      } catch (err) {
        console.error('Failed to fetch shapefile details:', err);
        setError('Failed to load shapefile details. Showing cached data.');
        // Keep using initial shapefile data if fetch fails
      } finally {
        setLoading(false);
      }
    };

    fetchShapefileDetails();
  }, [initialShapefile.id]);

  const isLoading = loading || externalLoading;
  
  const formatFileSize = (bytes: number): string => {
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(1)} MB`;
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleOpenModal = (feature: SpatialFeature) => {
    setSelectedFeature(feature);
    setIsModalOpen(true);
    setDownloadResponse(null);
    setIsDownloading(false);
    // Set default dates (last 30 days)
    const today = new Date();
    const thirtyDaysAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
    setEndDate(today.toISOString().split('T')[0]);
    setStartDate(thirtyDaysAgo.toISOString().split('T')[0]);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedFeature(null);
    setDownloadResponse(null);
    setIsDownloading(false);
    setStartDate('');
    setEndDate('');
  };

  const validateDates = (): { valid: boolean; error?: string } => {
    if (!startDate || !endDate) {
      return { valid: false, error: 'Both start and end dates are required' };
    }
    
    const start = new Date(startDate);
    const end = new Date(endDate);
    const today = new Date();
    
    if (start >= today || end >= today) {
      return { valid: false, error: 'Both dates must be in the past' };
    }
    
    if (start >= end) {
      return { valid: false, error: 'Start date must be earlier than end date' };
    }
    
    return { valid: true };
  };

  const handleDownloadChirps = async () => {
    if (!selectedFeature) return;
    
    const validation = validateDates();
    if (!validation.valid) {
      alert(validation.error);
      return;
    }

    setIsDownloading(true);
    
    try {
      const response = await fetch(`/api/climate/data/request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          region_id: selectedFeature.id,
          start_date: startDate,
          end_date: endDate,
          data_source: 'CHIRPS-3.0',
          variable: 'precipitation',
          force_download: false
        })
      });
      
      const data: ChirpsDownloadResponse = await response.json();
      setDownloadResponse(data);
    } catch (err) {
      console.error('Failed to download CHIRPS data:', err);
      setDownloadResponse({
        success: false,
        job_id: '',
        message: 'Failed to submit download request',
        status: 'error',
        processing_result: {
          success: false,
          error: err instanceof Error ? err.message : 'Unknown error',
          job_id: ''
        },
        timestamp: new Date().toISOString()
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-8">
          <LoaderIcon className="w-6 h-6 animate-spin text-blue-600 mr-2" />
          <span className="text-gray-600">Loading shapefile details...</span>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex">
            <InfoIcon className="w-5 h-5 text-yellow-400 mr-2 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-yellow-800">
              <p className="font-medium">Notice</p>
              <p>{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={onBack}
            className="flex items-center gap-2"
            disabled={isLoading}
          >
            <ArrowLeftIcon size={16} />
            Back to List
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <MapIcon className="w-6 h-6 text-blue-600" />
              {shapefile.name}
            </h1>
            <p className="text-gray-600 mt-1">
              Shapefile Details and Coverage Information
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => onDownload(shapefile)}
            disabled={isLoading}
            className="flex items-center gap-2"
          >
            <DownloadIcon size={16} />
            Download
          </Button>
          <StatusBadge variant={shapefile.isActive ? 'success' : 'error'}>
            {shapefile.isActive ? 'Active' : 'Inactive'}
          </StatusBadge>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Information */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Information Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <InfoIcon className="h-5 w-5" />
                Basic Information
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">File Name</dt>
                    <dd className="text-sm text-gray-900 mt-1">{shapefile.filename || shapefile.name}</dd>
                  </div>
                  {shapefile.description && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Description</dt>
                      <dd className="text-sm text-gray-900 mt-1">{shapefile.description}</dd>
                    </div>
                  )}
                  <div>
                    <dt className="text-sm font-medium text-gray-500">File Size</dt>
                    <dd className="text-sm text-gray-900 mt-1">
                      {shapefile.fileSize && shapefile.fileSize > 0 ? formatFileSize(shapefile.fileSize) : 'N/A'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Geometry Type</dt>
                    <dd className="text-sm text-gray-900 mt-1">
                      <StatusBadge variant="info">
                        {shapefile.geometryType || 'N/A'}
                      </StatusBadge>
                    </dd>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Feature Count</dt>
                    <dd className="text-sm text-gray-900 mt-1">{shapefile.featureCount ? shapefile.featureCount.toLocaleString() : 'N/A'}</dd>
                  </div>
                  {shapefile.regionType && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Region Type</dt>
                      <dd className="text-sm text-gray-900 mt-1 capitalize">{shapefile.regionType}</dd>
                    </div>
                  )}
                  {shapefile.adminLevel !== undefined && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Admin Level</dt>
                      <dd className="text-sm text-gray-900 mt-1">{shapefile.adminLevel}</dd>
                    </div>
                  )}
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Status</dt>
                    <dd className="text-sm text-gray-900 mt-1">
                      <StatusBadge variant={shapefile.isActive ? 'success' : 'error'}>
                        {shapefile.isActive ? 'Active' : 'Inactive'} 
                        {shapefile.validationStatus && ` (${shapefile.validationStatus})`}
                      </StatusBadge>
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">File ID</dt>
                    <dd className="text-sm text-gray-600 mt-1 font-mono text-xs">{shapefile.id}</dd>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Separator />

          {/* Geographic Information Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPinIcon className="h-5 w-5" />
                Geographic Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Bounding Box Map */}
              {shapefile.boundingBox && (
                <div data-map-section>
                  <dt className="text-sm font-medium text-gray-500 mb-3">Location Map</dt>
                  <BoundingBoxMap 
                    boundingBox={shapefile.boundingBox}
                    height="300px"
                    className="w-full"
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    Interactive map showing the geographic extent of this shapefile
                  </p>
                </div>
              )}

              {/* Coordinate Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Total Area</dt>
                    <dd className="text-sm text-gray-900 mt-1">
                      {shapefile.totalAreaKm2 ? 
                        `${shapefile.totalAreaKm2.toLocaleString('en-US', { maximumFractionDigits: 2 })} km²` : 
                        'N/A'
                      }
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Bounding Box</dt>
                    <dd className="text-sm text-gray-900 mt-1 font-mono">
                      {shapefile.boundingBox ? 
                        `${shapefile.boundingBox.minLat.toFixed(4)}, ${shapefile.boundingBox.minLng.toFixed(4)} to ${shapefile.boundingBox.maxLat.toFixed(4)}, ${shapefile.boundingBox.maxLng.toFixed(4)}` : 
                        'N/A'
                      }
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Latitude Range</dt>
                    <dd className="text-sm text-gray-900 mt-1">
                      {shapefile.boundingBox ? 
                        `${shapefile.boundingBox.minLat.toFixed(4)}° to ${shapefile.boundingBox.maxLat.toFixed(4)}°` : 
                        'N/A'
                      }
                    </dd>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Longitude Range</dt>
                    <dd className="text-sm text-gray-900 mt-1">
                      {shapefile.boundingBox ? 
                        `${shapefile.boundingBox.minLng.toFixed(4)}° to ${shapefile.boundingBox.maxLng.toFixed(4)}°` : 
                        'N/A'
                      }
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Center Point</dt>
                    <dd className="text-sm text-gray-900 mt-1">
                      {shapefile.boundingBox ? 
                        `${((shapefile.boundingBox.minLat + shapefile.boundingBox.maxLat) / 2).toFixed(4)}°, ${((shapefile.boundingBox.minLng + shapefile.boundingBox.maxLng) / 2).toFixed(4)}°` : 
                        'N/A'
                      }
                    </dd>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>



          {/* Spatial Features Section */}
          {shapefile.spatialFeatures && shapefile.spatialFeatures.length > 0 && (
            <>
              <Separator />
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <LayersIcon className="h-5 w-5" />
                    Spatial Features ({shapefile.spatialFeaturesCount || shapefile.spatialFeatures.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full table-auto">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Feature Name</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Code</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Region Type</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Admin Level</th>
                          <th className="text-right py-3 px-4 text-sm font-medium text-gray-500">Area (km²)</th>
                          <th className="text-center py-3 px-4 text-sm font-medium text-gray-500">Centroid</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">ISO Alpha2</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">ISO Alpha3</th>
                          <th className="text-center py-3 px-4 text-sm font-medium text-gray-500">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {shapefile.spatialFeatures.map((feature: SpatialFeature) => (
                          <tr key={feature.id} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="py-3 px-4 text-sm text-gray-900 font-medium">{feature.feature_name}</td>
                            <td className="py-3 px-4 text-sm text-gray-600 font-mono">{feature.feature_code}</td>
                            <td className="py-3 px-4 text-sm text-gray-600 capitalize">{feature.region_type}</td>
                            <td className="py-3 px-4 text-sm text-gray-600">
                              {typeof feature.admin_level === 'number' ? feature.admin_level : 'N/A'}
                            </td>
                            <td className="py-3 px-4 text-sm text-gray-600 text-right">
                              {feature.area_km2.toLocaleString('en-US', { maximumFractionDigits: 2 })}
                            </td>
                            <td className="py-3 px-4 text-sm text-gray-600 text-center font-mono">
                              {feature.centroid.lat.toFixed(4)}, {feature.centroid.lon.toFixed(4)}
                            </td>
                            <td className="py-3 px-4 text-sm text-gray-600 font-mono">
                              {typeof feature.iso_alpha2 === 'string' ? feature.iso_alpha2 : 'N/A'}
                            </td>
                            <td className="py-3 px-4 text-sm text-gray-600 font-mono">
                              {typeof feature.iso_alpha3 === 'string' ? feature.iso_alpha3 : 'N/A'}
                            </td>
                            <td className="py-3 px-4 text-sm text-center">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleOpenModal(feature)}
                                className="flex items-center gap-2"
                              >
                                <EyeIcon size={14} />
                                View
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => onDownload(shapefile)}
                disabled={isLoading}
              >
                <DownloadIcon size={16} />
                Download Shapefile
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start"
                disabled={!shapefile.boundingBox}
                onClick={() => {
                  const mapElement = document.querySelector('[data-map-section]');
                  if (mapElement) {
                    mapElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }
                }}
              >
                <MapIcon size={16} />
                View on Map
                {!shapefile.boundingBox && (
                  <span className="ml-auto text-xs text-gray-500">No Coordinates</span>
                )}
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start"
                disabled={true}
              >
                <BarChart3Icon size={16} />
                Coverage Analysis
                <span className="ml-auto text-xs text-gray-500">Coming Soon</span>
              </Button>
            </CardContent>
          </Card>

          {/* Climate Data Coverage Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3Icon className="h-5 w-5" />
                Climate Data Details
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Data Points</dt>
                  <dd className="text-sm text-gray-900 mt-1">{shapefile.coverage?.dataPoints ? shapefile.coverage.dataPoints.toLocaleString() : 'N/A'}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Weather Stations</dt>
                  <dd className="text-sm text-gray-900 mt-1">{shapefile.coverage?.stations ? shapefile.coverage.stations.toLocaleString() : 'N/A'}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Data Completeness</dt>
                  <dd className="text-sm text-gray-900 mt-1">
                    {shapefile.coverage?.completeness ? `${shapefile.coverage.completeness.toFixed(1)}%` : 'N/A'}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Coverage Status</dt>
                  <dd className="text-sm text-gray-900 mt-1">
                    {shapefile.coverage ? (
                      <StatusBadge variant={shapefile.coverage.completeness > 80 ? 'success' : shapefile.coverage.completeness > 50 ? 'warning' : 'error'}>
                        {shapefile.coverage.completeness > 80 ? 'Excellent' : shapefile.coverage.completeness > 50 ? 'Good' : 'Poor'}
                      </StatusBadge>
                    ) : 'N/A'}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Coverage Summary</dt>
                  <dd className="text-sm text-gray-900 mt-1">
                    {shapefile.coverage && shapefile.coverage.dataPoints && shapefile.coverage.stations ? 
                      `${shapefile.coverage.dataPoints.toLocaleString()} data points from ${shapefile.coverage.stations.toLocaleString()} stations` : 
                      'No coverage data available'
                    }
                  </dd>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* File Details Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileIcon className="h-5 w-5" />
                File Details
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500">File ID</dt>
                  <dd className="text-xs text-gray-900 mt-1 font-mono break-all">{shapefile.id}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Uploaded</dt>
                  <dd className="text-sm text-gray-900 mt-1 flex items-center gap-2">
                    <CalendarIcon size={16} className="text-gray-400" />
                    {formatDate(shapefile.uploadedAt || shapefile.createdAt)}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Created Date</dt>
                  <dd className="text-sm text-gray-900 mt-1 flex items-center gap-2">
                    <CalendarIcon size={16} className="text-gray-400" />
                    {formatDate(shapefile.createdAt)}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Last Modified</dt>
                  <dd className="text-sm text-gray-900 mt-1 flex items-center gap-2">
                    <CalendarIcon size={16} className="text-gray-400" />
                    {formatDate(shapefile.updatedAt)}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">File Format</dt>
                  <dd className="text-sm text-gray-900 mt-1">Shapefile (.shp)</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Source</dt>
                  <dd className="text-sm text-gray-900 mt-1">Climate Data API</dd>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Summary Stats */}
          {shapefile.coverage && shapefile.featureCount && shapefile.featureCount > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Coverage Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Data Quality</span>
                    <span className={`text-sm font-medium ${
                      shapefile.coverage.completeness >= 90 ? 'text-green-600' :
                      shapefile.coverage.completeness >= 70 ? 'text-yellow-600' : 'text-red-600'
                    }`}>
                      {shapefile.coverage.completeness >= 90 ? 'Excellent' :
                       shapefile.coverage.completeness >= 70 ? 'Good' : 'Limited'}
                    </span>
                  </div>
                  {shapefile.coverage.dataPoints && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Data Density</span>
                      <span className="text-sm font-medium">
                        {(shapefile.coverage.dataPoints / shapefile.featureCount).toFixed(1)} pts/feature
                      </span>
                    </div>
                  )}
                  {shapefile.coverage.stations && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Station Coverage</span>
                      <span className="text-sm font-medium">
                        {(shapefile.coverage.stations / shapefile.featureCount).toFixed(2)} stations/feature
                      </span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Modal for Spatial Feature Details and CHIRPS Download */}
      {isModalOpen && selectedFeature && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Spatial Feature Details - {selectedFeature.feature_name}
              </h3>
              <Button
                variant="outline"
                size="sm"
                onClick={handleCloseModal}
                className="flex items-center gap-2"
              >
                <XIcon size={16} />
                Close
              </Button>
            </div>

            {/* Feature Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-gray-500">Feature Name</label>
                  <p className="text-sm text-gray-900">{selectedFeature.feature_name}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Feature Code</label>
                  <p className="text-sm text-gray-900 font-mono">{selectedFeature.feature_code}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Region Type</label>
                  <p className="text-sm text-gray-900 capitalize">{selectedFeature.region_type}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Admin Level</label>
                  <p className="text-sm text-gray-900">
                    {typeof selectedFeature.admin_level === 'number' ? selectedFeature.admin_level : 'N/A'}
                  </p>
                </div>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-gray-500">Area (km²)</label>
                  <p className="text-sm text-gray-900">
                    {selectedFeature.area_km2.toLocaleString('en-US', { maximumFractionDigits: 2 })}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Centroid</label>
                  <p className="text-sm text-gray-900 font-mono">
                    {selectedFeature.centroid.lat.toFixed(4)}, {selectedFeature.centroid.lon.toFixed(4)}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">ISO Alpha2</label>
                  <p className="text-sm text-gray-900 font-mono">
                    {typeof selectedFeature.iso_alpha2 === 'string' ? selectedFeature.iso_alpha2 : 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">ISO Alpha3</label>
                  <p className="text-sm text-gray-900 font-mono">
                    {typeof selectedFeature.iso_alpha3 === 'string' ? selectedFeature.iso_alpha3 : 'N/A'}
                  </p>
                </div>
              </div>
            </div>

            <Separator />

            {/* CHIRPS Download Section */}
            <div className="mt-6">
              <h4 className="text-md font-semibold text-gray-900 mb-4">Download CHIRPS Data</h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    max={new Date().toISOString().split('T')[0]}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={isDownloading}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    max={new Date().toISOString().split('T')[0]}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={isDownloading}
                  />
                </div>
              </div>

              <div className="flex gap-3 mb-4">
                <Button
                  onClick={handleDownloadChirps}
                  disabled={isDownloading || !startDate || !endDate}
                  className="flex items-center gap-2"
                >
                  {isDownloading ? (
                    <>
                      <LoaderIcon size={16} className="animate-spin" />
                      Downloading...
                    </>
                  ) : (
                    <>
                      <DownloadIcon size={16} />
                      Download CHIRPS Data
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  onClick={handleCloseModal}
                  disabled={isDownloading}
                >
                  Cancel
                </Button>
              </div>

              {/* Download Response */}
              {downloadResponse && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <h5 className="font-medium text-gray-900 mb-2">Download Response</h5>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Success:</span>
                      <span className={downloadResponse.success ? 'text-green-600' : 'text-red-600'}>
                        {downloadResponse.success ? 'Yes' : 'No'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Job ID:</span>
                      <span className="font-mono text-gray-900">{downloadResponse.job_id || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Status:</span>
                      <StatusBadge variant={downloadResponse.status === 'success' ? 'success' : 'error'}>
                        {downloadResponse.status}
                      </StatusBadge>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Message:</span>
                      <span className="text-gray-900">{downloadResponse.message}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Timestamp:</span>
                      <span className="text-gray-900">{downloadResponse.timestamp}</span>
                    </div>
                    {downloadResponse.processing_result && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <h6 className="font-medium text-gray-900 mb-1">Processing Result</h6>
                        <div className="space-y-1">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Processing Success:</span>
                            <span className={downloadResponse.processing_result.success ? 'text-green-600' : 'text-red-600'}>
                              {downloadResponse.processing_result.success ? 'Yes' : 'No'}
                            </span>
                          </div>
                          {downloadResponse.processing_result.error && (
                            <div className="flex justify-between">
                              <span className="text-gray-600">Error:</span>
                              <span className="text-red-600">{downloadResponse.processing_result.error}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 